import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { Button, IconButton } from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import LaunchIcon from '@material-ui/icons/Launch';
import { formatFirestoreTimestamp } from '../_helpers/mappers';
import JsonModal from './JsonModal';
import SetPublishStatusModal from './SetPublishStatusModal';
import ChangeStoreStatusDialog from './ChangeStoreStatusDialog';
import config from '../_helpers/config'
import UpdateStoreDataDialog from './UpdateStoreDataDialog';
import PublishWizard from '../PublishWizard';
import { baseDeeplink } from '../_constants/baseDeeplink';
import { QRCodeSVG } from 'qrcode.react';
import DeleteFirebaseAppsDialog from './DeleteFirebaseAppsDialog';
import UnlinkExpoProjectDialog from './UnlinkExpoProjectDialog';
import ConfigurationModal from './ConfigurationModal';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    overflowWrap: 'anywhere',
    padding: theme.spacing(2),
  },
  propertyContainer: {
    '& a': {
      color: 'black',
      textDecoration: 'none'
    }
  },
  label: {
    fontWeight: 'bold',
    marginBottom: 0
  },
  value: {
    display: '-webkit-box',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical'
  },
  iconButton: {
    marginLeft: 5
  },
  icon: {
    fontSize: 16
  },
  image: {
    border: 'solid 1px #ececec',
    height: 'auto',
    width: 70
  },
  buttonsContainer: {
    '& > *': {
      marginRight: theme.spacing(2),
    },
  },
  qrCode: {
    marginTop: 10
  }
}));

export default function DetailsPanel({ onDataUpdate, platformId, store }) {
  const classes = useStyles();

  const [showChangeStoreStatusDialog, setShowChangeStoreStatusDialog] = useState(false);
  const [showConfigurationModal, setShowConfigurationModal] = useState(false);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [showSetPublishStatusModal, setShowSetPublishStatusModal] = useState(false);
  const [showUpdateStoreDataDialog, setShowUpdateStoreDataDialog] = useState(false);
  const [showPublishWizard, setShowPublishWizard] = useState(false);
  const [showDeleteFirebaseAppsDialog, setShowDeleteFirebaseAppsDialog] = useState(false);
  const [showUnlinkExpoProjectDialog, setShowUnlinkExpoProjectDialog] = useState(false);

  const accessTokenFieldName = 'accessToken';

  const createdAtFieldName = 'createdAt';
  const updatedAtFieldName = 'updatedAt';

  const androidData = _.get(store, 'platforms.android', {});
  const iOSData = _.get(store, 'platforms.ios', {});

  const copyToClipboard = (value) =>
    navigator.clipboard.writeText(value)

  function TextProperty({ data, label, name }) {
    const isWhatsNew = label === 'O que há de novo nesta versão'

    return (
      <div className={classes.propertyContainer}>
        <p className={classes.label}> {`${label}`}
          <IconButton className={classes.iconButton} onClick={() => copyToClipboard(isWhatsNew ? name : data[name])}>
            <FileCopyIcon className={classes.icon} />
          </IconButton>
        </p>
        {isWhatsNew ? (
          <p className={classes.value}>{name}</p>
        ) : (
          <p className={classes.value}>{data[name] || '-'}</p>
        )}
      </div>
    )
  }

  function ImageProperty({ data, label, name }) {
    return (
      <div className={classes.propertyContainer}>
        <a href={data[name]} target="_blank">
          <p className={classes.label}> {`${label}`}
            <IconButton className={classes.iconButton}>
              <LaunchIcon className={classes.icon} />
            </IconButton>
          </p>
          {_.isEmpty(data[name]) ? '-' : <img className={classes.image} src={data[name]} />}
        </a>
      </div>
    )
  }

  function DateProperty({ data, label, name }) {
    return (
      <div className={classes.propertyContainer}>
        <p className={classes.label}> {`${label}`}</p>
        <p className={classes.value}>{formatFirestoreTimestamp(data[name], 'DD/MM/YYYY HH:mm') || '-'}</p>
      </div>
    )
  }

  function BooleanProperty({ data, label, name }) {
    return (
      <div className={classes.propertyContainer}>
        <p className={classes.label}> {`${label}`}</p>
        <p className={classes.value}>{data[name] ? 'Sim' : 'Não'}</p>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <Grid container spacing={4}>
        <Grid item xs={2}>
          <TextProperty data={store} label="ID" name={'id'} />
          <TextProperty data={store} label="Access token" name={accessTokenFieldName} />
          <TextProperty data={store} label="Slug" name="slug" />
          <TextProperty data={store} label="ID do Projeto Expo" name="expoProjectId" />
          <TextProperty data={store} label="Nome da conta Expo" name="expoAccountName" />
          <TextProperty data={store} label="Status" name="status" />
          <TextProperty data={store} label="Tema ativo" name="activeTheme" />
          <TextProperty data={store} label="Projeto do Firebase" name="firebaseProjectName" />
          <DateProperty data={store} label="Fim do período de testes" name="trialEndsAt" />
          <DateProperty data={store} label="Data de cadastro" name={createdAtFieldName} />
          <DateProperty data={store} label="Última atualização" name={updatedAtFieldName} />
          <DateProperty data={store} label="Data de saída" name="churnedAt" />
        </Grid>

        <Grid item xs={3}>
          <TextProperty data={store} label="Título do aplicativo" name="title" />
          <TextProperty data={store} label="Descrição curta" name="shortDescription" />
          <TextProperty data={store} label="Descrição" name="description" />
          <TextProperty label='O que há de novo nesta versão' name='Melhorias e ajustes para tornar sua experiência ainda melhor.' />
          <TextProperty data={store} label="Url da Política de Privacidade" name="privacyPolicyUrl" />
          <TextProperty data={store} label="Palavras chave" name="keywords" />
          <TextProperty data={store} label="Vídeo promocional" name="promotionalVideo" />
          <TextProperty data={store} label="ID do Facebook" name="facebookId" />
          <TextProperty data={store} label="Token do Facebook" name="facebookClientToken" />
          <BooleanProperty data={store} label="O app é Exclusivo?" name="isAppExclusive" />
          {store.platformId === 'nuvemshop' && <TextProperty data={store} label="Plano da Nuvemshop" name="nuvemshopPlanName" />}
        </Grid>

        <Grid item xs={3}>
          <h2>Android</h2>
          <TextProperty data={androidData} label="Bundle ID" name="bundleId" />
          <TextProperty data={androidData} label="Build version" name="currentBuildVersion" />
          <TextProperty data={androidData} label="ID do Aplicativo Firebase" name="firebaseApplicationId" />
          <TextProperty data={androidData} label="Url" name="downloadUrl" />
          <TextProperty data={androidData} label="Status" name="publishStatus" />
          <TextProperty data={androidData} label="Nome da conta Google" name="developerAccountName" />
          <TextProperty data={androidData} label="Email da conta Google" name="developerAccountEmail" />
          <TextProperty data={androidData} label="Nome da conta de serviço" name="googlePlayServiceAccountName" />
          <DateProperty data={androidData} label="Última solicitação de publicação" name="lastPublishRequestedAt" />
          <DateProperty data={androidData} label="Última atualização de status" name="publishStatusUpdatedAt" />
        </Grid>

        <Grid item xs={2}>
          <h2>iOS</h2>
          <TextProperty data={iOSData} label="Package name" name="packageName" />
          <TextProperty data={iOSData} label="Build version" name="currentBuildVersion" />
          <TextProperty data={iOSData} label="ID do Aplicativo Firebase" name="firebaseApplicationId" />
          <TextProperty data={iOSData} label="Url" name="downloadUrl" />
          <TextProperty data={iOSData} label="Status" name="publishStatus" />
          <TextProperty data={iOSData} label="Team Name" name="teamName" />
          <TextProperty data={iOSData} label="Team ID" name="teamID" />
          <TextProperty data={iOSData} label="ID da App Store Connect" name="ascAppId" />
          <BooleanProperty data={iOSData} label="Suporta iPad?" name="supportsTablet" />
          <DateProperty data={iOSData} label="Última solicitação de publicação" name="lastPublishRequestedAt" />
          <DateProperty data={iOSData} label="Última atualização de status" name="publishStatusUpdatedAt" />
        </Grid>

        <Grid item xs={2}>
          <ImageProperty data={store} label="Ícone" name="iconUrl" />
          <ImageProperty data={store} label="Tela de introdução" name="launchScreenUrl" />
          <ImageProperty data={store} label="Banner da Play Store" name="featureGraphicUrl" />
          <QRCodeSVG
            className={classes.qrCode}
            value={`${baseDeeplink}&deep_link_value=${store.platformId}&deep_link_sub1=${store.id}`}
            size={100}
          />
          <br />
          <div>
            <a href={`${baseDeeplink}&deep_link_value=${store.platformId}&deep_link_sub1=${store.id}`} target="_blank">
              Abrir no visualizador
            </a>
          </div>
        </Grid>

        <Grid alignItems="center" className={classes.buttonsContainer} container direction="row" item justify="flex-start" xs={12}>
          <Button variant="contained" onClick={() => setShowConfigurationModal(true)}>Configurações</Button>
          <Button variant="contained" onClick={() => setShowSetPublishStatusModal(true)}>Alterar status de publicação</Button>
          <Button variant="contained" onClick={() => setShowChangeStoreStatusDialog(true)}>Alterar status da loja</Button>
          <Button variant="contained" onClick={() => setShowUpdateStoreDataDialog(true)}>Dados de publicação</Button>
          <Button variant="contained" color="primary" onClick={() => setShowPublishWizard(true)}>
            Publicar aplicativo
          </Button>
          <Button variant="contained" onClick={() => setShowJsonModal(true)}>Ver JSON</Button>
          {store.status === 'archived' && store.firebaseProjectName && (
            <Button variant="contained" onClick={() => setShowDeleteFirebaseAppsDialog(true)}>Excluir apps do firebase</Button>
          )}
          {store.status === 'archived' && store.expoProjectId && (
            <Button variant="contained" onClick={() => setShowUnlinkExpoProjectDialog(true)}>Desvincular projeto Expo</Button>
          )}
        </Grid>
      </Grid>

      <UpdateStoreDataDialog
        data={store}
        isOpen={showUpdateStoreDataDialog}
        onClose={() => setShowUpdateStoreDataDialog(false)}
        onDataUpdate={onDataUpdate}
        platformId={platformId}
      />

      <ChangeStoreStatusDialog
        data={store}
        isOpen={showChangeStoreStatusDialog}
        onClose={() => setShowChangeStoreStatusDialog(false)}
        onDataUpdate={onDataUpdate}
        platformId={platformId}
      />

      <PublishWizard
        data={store}
        isOpen={showPublishWizard}
        onClose={() => setShowPublishWizard(false)}
        onDataUpdate={onDataUpdate}
        platformId={platformId}
      />

      <JsonModal
        data={store}
        isOpen={showJsonModal}
        onClose={() => setShowJsonModal(false)}
      />

      <DeleteFirebaseAppsDialog
        data={store}
        isOpen={showDeleteFirebaseAppsDialog}
        onClose={() => setShowDeleteFirebaseAppsDialog(false)}
        onDataUpdate={onDataUpdate}
        platformId={platformId}
      />

      <UnlinkExpoProjectDialog
        data={store}
        isOpen={showUnlinkExpoProjectDialog}
        onClose={() => setShowUnlinkExpoProjectDialog(false)}
        onDataUpdate={onDataUpdate}
        platformId={platformId}
      />

      <SetPublishStatusModal
        data={store}
        isOpen={showSetPublishStatusModal}
        onClose={() => setShowSetPublishStatusModal(false)}
        onDataUpdate={onDataUpdate}
        platformId={platformId}
      />

      <ConfigurationModal
        data={store}
        isOpen={showConfigurationModal}
        onClose={() => setShowConfigurationModal(false)}
        onDataUpdate={onDataUpdate}
        platformId={platformId}
      />
    </div>
  );
}